/**
 * Kit de difusión del portal de clientes: imagen vertical (1080×1920, formato
 * de estado de WhatsApp) con el nombre del negocio, un mensaje, el QR y el
 * enlace. WhatsApp no tiene API para publicar estados, así que el negocio la
 * comparte a mano: "Compartir" abre la hoja del sistema con la imagen (de ahí
 * WhatsApp > Mi estado) y, donde el navegador no comparte archivos, se descarga.
 *
 * Varias plantillas para rotar: el estado dura 24 horas y repetir la misma
 * imagen deja de verse.
 */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import QRCode from "qrcode";
import { useGlobalMDAlert } from "context";
import { FP } from "components/financepro";
import { urlPortal } from "lib/portalqr";

const ANCHO = 1080;
const ALTO = 1920;
const FUENTE = "Roboto, Helvetica, Arial, sans-serif";

export const PLANTILLAS = [
  {
    clave: "cuenta",
    etiqueta: "Tu cuenta",
    titulo: "Consulta tu cuenta desde tu celular",
    texto: "Balance, cuotas y recibos. Sin llamar y sin hacer fila.",
  },
  {
    clave: "cuota",
    etiqueta: "Próxima cuota",
    titulo: "¿Cuándo vence tu próxima cuota?",
    texto: "Mírala en segundos desde tu celular, a cualquier hora.",
  },
  {
    clave: "recibos",
    etiqueta: "Recibos",
    titulo: "Tus recibos siempre a mano",
    texto: "Revisa cada pago que has hecho y vuelve a imprimirlo cuando quieras.",
  },
];

// Parte el texto en líneas que quepan en `ancho` con la fuente ya puesta.
export function partirLineas(ctx, texto, ancho) {
  const lineas = [];
  let actual = "";
  String(texto || "")
    .split(/\s+/)
    .filter(Boolean)
    .forEach((palabra) => {
      const prueba = actual ? `${actual} ${palabra}` : palabra;
      if (actual && ctx.measureText(prueba).width > ancho) {
        lineas.push(actual);
        actual = palabra;
      } else {
        actual = prueba;
      }
    });
  if (actual) lineas.push(actual);
  return lineas;
}

function parrafo(ctx, texto, y, { fuente, color, alto, ancho = ANCHO - 200 }) {
  ctx.font = fuente;
  ctx.fillStyle = color;
  let cursor = y;
  partirLineas(ctx, texto, ancho).forEach((linea) => {
    ctx.fillText(linea, ANCHO / 2, cursor);
    cursor += alto;
  });
  return cursor;
}

function rectRedondo(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Todo el contenido queda entre y=260 y y=1660: WhatsApp tapa el resto con el
// nombre del contacto arriba y la caja de respuesta abajo.
function dibujar(canvas, { negocio, plantilla, qr, enlaceVisible }) {
  const ctx = canvas.getContext("2d");
  const fondo = ctx.createLinearGradient(0, 0, 0, ALTO);
  fondo.addColorStop(0, "#0E1F33");
  fondo.addColorStop(1, "#16314F");
  ctx.fillStyle = fondo;
  ctx.fillRect(0, 0, ANCHO, ALTO);

  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  let y = 270;
  y = parrafo(ctx, String(negocio || "").toUpperCase(), y, {
    fuente: `700 44px ${FUENTE}`,
    color: "#8FB8FF",
    alto: 56,
  });
  y = parrafo(ctx, plantilla.titulo, y + 40, {
    fuente: `800 92px ${FUENTE}`,
    color: "#FFFFFF",
    alto: 104,
  });
  y = parrafo(ctx, plantilla.texto, y + 36, {
    fuente: `400 46px ${FUENTE}`,
    color: "#D5E2F2",
    alto: 62,
  });

  // Tarjeta blanca con el QR, centrada en el espacio que queda.
  const lado = 560;
  const margen = 44;
  const tarjeta = lado + margen * 2;
  const pieY = 1660 - 210;
  const tarjetaY = Math.max(y + 50, y + (pieY - y - tarjeta) / 2);
  ctx.fillStyle = "#FFFFFF";
  rectRedondo(ctx, (ANCHO - tarjeta) / 2, tarjetaY, tarjeta, tarjeta, 48);
  ctx.fill();
  ctx.drawImage(qr, (ANCHO - lado) / 2, tarjetaY + margen, lado, lado);

  let pie = tarjetaY + tarjeta + 44;
  pie = parrafo(ctx, "Escanea el código o entra en", pie, {
    fuente: `400 40px ${FUENTE}`,
    color: "#D5E2F2",
    alto: 54,
  });
  pie = parrafo(ctx, enlaceVisible, pie + 4, {
    fuente: `700 46px ${FUENTE}`,
    color: "#FFFFFF",
    alto: 58,
  });
  parrafo(ctx, "Solo necesitas tu número de cédula", pie + 14, {
    fuente: `400 36px ${FUENTE}`,
    color: "#8FB8FF",
    alto: 48,
  });
}

const aBlob = (canvas) =>
  new Promise((resolve) => {
    canvas.toBlob(resolve, "image/png");
  });

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
// El tema no pone color de texto legible en los botones: va explícito, como en
// el resto de pantallas FinancePro.
const btnSolid = {
  ...btnBase,
  bgcolor: FP.color.navy,
  color: "#FFFFFF",
  "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${FP.color.navy}66`, color: "#FFFFFF" },
};
const btnOutline = {
  ...btnBase,
  color: FP.color.navy,
  bgcolor: "#FFFFFF",
  border: `1px solid ${FP.color.navy}`,
  "&:hover": {
    bgcolor: `${FP.color.navy}12`,
    border: `1px solid ${FP.color.navy}`,
    color: FP.color.navy,
  },
  "&.Mui-disabled": { color: `${FP.color.navy}66`, border: `1px solid ${FP.color.navy}44` },
};

export default function PortalKit({ negocio, ruta }) {
  const { showAlert } = useGlobalMDAlert();
  const canvasRef = useRef(null);
  const [clave, setClave] = useState(PLANTILLAS[0].clave);
  const [lista, setLista] = useState(false);
  const plantilla = PLANTILLAS.find((p) => p.clave === clave) || PLANTILLAS[0];
  // `o=estado` separa en la analítica del portal las visitas que vienen de aquí.
  const enlace = urlPortal("estado", ruta);
  const enlaceVisible = enlace.replace(/^https?:\/\//, "").replace(/\/\?o=.*$/, "");

  useEffect(() => {
    let activo = true;
    setLista(false);
    (async () => {
      const qr = document.createElement("canvas");
      await QRCode.toCanvas(qr, enlace, { margin: 0, width: 560, errorCorrectionLevel: "M" });
      // La fuente debe estar cargada antes de pintar o el canvas cae en la de respaldo.
      if (document.fonts?.load) {
        await Promise.all([
          document.fonts.load(`800 92px ${FUENTE}`),
          document.fonts.load(`400 46px ${FUENTE}`),
        ]).catch(() => {});
      }
      if (!activo || !canvasRef.current) return;
      dibujar(canvasRef.current, { negocio, plantilla, qr, enlaceVisible });
      setLista(true);
    })().catch(() => {});
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, negocio, enlace]);

  const nombreArchivo = `portal-${plantilla.clave}.png`;

  const descargar = async () => {
    const blob = await aBlob(canvasRef.current);
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = nombreArchivo;
    a.click();
    setTimeout(() => URL.revokeObjectURL(href), 10000);
  };

  const compartir = async () => {
    const blob = await aBlob(canvasRef.current);
    const archivo = new File([blob], nombreArchivo, { type: "image/png" });
    if (navigator.canShare?.({ files: [archivo] })) {
      try {
        await navigator.share({ files: [archivo], text: `${plantilla.titulo}: ${enlace}` });
      } catch (e) {
        // El usuario cerró la hoja de compartir.
      }
      return;
    }
    await descargar();
    showAlert({
      message: "Este navegador no comparte imágenes: se descargó para subirla a tu estado.",
      color: "info",
    });
  };

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ sm: "center" }}>
      {/* <canvas> directo: en un Box de MUI width/height pasan a ser CSS y el
          lienzo se queda en 300×150. */}
      <canvas
        ref={canvasRef}
        width={ANCHO}
        height={ALTO}
        aria-label="Imagen para el estado de WhatsApp"
        style={{
          width: 170,
          height: Math.round((170 * ALTO) / ANCHO),
          flexShrink: 0,
          alignSelf: "center",
          borderRadius: 12,
          boxShadow: FP.shadow.soft,
          background: "#0E1F33",
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, mb: 1.2 }}>
          Elige un mensaje y súbelo a tu estado de WhatsApp. Cámbialo cada semana: el estado dura 24
          horas. La misma imagen sirve impresa como cartel para el mostrador.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.6 }}>
          {PLANTILLAS.map((p) => (
            <Chip
              key={p.clave}
              label={p.etiqueta}
              onClick={() => setClave(p.clave)}
              sx={{
                fontWeight: 700,
                bgcolor: p.clave === clave ? FP.color.navy : FP.color.border,
                color: p.clave === clave ? "#FFFFFF" : FP.color.textSecondary,
                "&:hover": { bgcolor: p.clave === clave ? FP.color.navy : FP.color.border },
              }}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<IosShareOutlinedIcon />}
            sx={btnSolid}
            disabled={!lista}
            onClick={compartir}
          >
            Compartir
          </Button>
          <Button
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            sx={btnOutline}
            disabled={!lista}
            onClick={descargar}
          >
            Descargar
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}

PortalKit.propTypes = { negocio: PropTypes.string, ruta: PropTypes.string };
PortalKit.defaultProps = { negocio: "", ruta: "/portal" };
